import { useIsomorphicLayoutEffect } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { variants } from "../Input";
import { cn } from "../utils";

export type TextAreaProps = React.DetailedHTMLProps<
	React.TextareaHTMLAttributes<HTMLTextAreaElement>,
	HTMLTextAreaElement
>;

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
	function TextAreaComponent({ className, style, ...props }, incomingRef) {
		const container = useRef<HTMLDivElement | null>(null);
		const textbox = useRef<HTMLTextAreaElement | null>(null);
		const bufferbox = useRef<HTMLTextAreaElement | null>(null);
		const [height, setHeight] = useState("0px");

		const updateHeight = useCallback(() => {
			const box = textbox.current;
			const buf = bufferbox.current;

			if (box && buf) {
				buf.style.height = "0px";

				if (buf.value.slice(-1) === "\n") {
					buf.value += " ";
					box.value = buf.value;
				}

				const heightString = `${Math.ceil(buf.scrollHeight)}px`;

				box.style.height = heightString;

				if (heightString !== height) {
					setHeight(heightString);
				}
			}
		}, [height]);

		useEffect(() => {
			const containerEl = container.current;

			if (!containerEl) {
				return undefined;
			}

			const resizeObserver = new ResizeObserver(() => {
				updateHeight();
			});

			// watch for resize events
			resizeObserver.observe(containerEl);

			// cleanup
			return () => {
				resizeObserver.disconnect();
			};
		}, [updateHeight]);

		useIsomorphicLayoutEffect(() => {
			updateHeight();
		}, [props.value]);

		// IMPORTANT: pb-1 to avoid cutting off the bottoms of lowercase y, j, g, p, q, and other
		// letters that have a stem.
		const classNames = variants({ size: "md", elevation: "flat", className });
		const styles = useMemo(() => {
			return Object.assign(style || {}, {
				boxSizing: "border-box",
				resize: "none",
				overflow: "hidden",
				width: "100%",
				height,
			});
		}, [style, height]);

		const sharedRefs = (el: HTMLTextAreaElement) => {
			textbox.current = el;

			if (!incomingRef) {
				return undefined;
			}

			if (typeof incomingRef === "function") {
				incomingRef(el);
			} else {
				incomingRef.current = el;
			}
		};

		return (
			<div className={cn("w-full", "print:hidden")} ref={container}>
				{/* This textarea is visible */}
				<textarea
					ref={sharedRefs}
					rows={1}
					className={classNames}
					style={styles}
					{...props}
				/>
				{/* We use the scroll height of this invisible box to detect height changes */}
				<textarea
					aria-hidden
					ref={bufferbox}
					rows={1}
					className={classNames}
					style={{
						width: "100%",
						height: "0px",
						display: "block",
						visibility: "hidden",
						pointerEvents: "none",
						resize: "none",
						overflow: "hidden",
					}}
					value={props.value || "&nbsp;"}
					disabled
				/>
			</div>
		);
	}
);
